import {
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { KeyboardBackspace } from "@material-ui/icons";
import React, { useEffect, useRef } from "react";

const useStyles = makeStyles((theme) => ({
  trail: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    textAlign: "left",
  },
  backBtn: {
    padding: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  // One line that scrolls sideways when the trail is longer than the screen,
  // collapsed or expanded; names are never cut.
  crumbs: {
    flex: 1,
    minWidth: 0,
    overflowX: "auto",
    "& .MuiBreadcrumbs-ol": {
      flexWrap: "nowrap",
    },
    "& .MuiBreadcrumbs-li": {
      flexShrink: 0,
    },
    "& .MuiBreadcrumbs-separator": {
      color: theme.palette.text.primary,
    },
    // A thin scrollbar in the platform's primary colour. index.css hides every
    // ::-webkit-scrollbar (0px), which these class-scoped rules outrank. Chromium only
    // paints ::-webkit-scrollbar while the standard scrollbar-* properties are unset,
    // and ACCB's accbScrollbars.css sets them on every element, so they are reset here
    // ("&&" outranks its `html.accb-scrollbars *`); elsewhere this is the default anyway.
    "&&": {
      scrollbarWidth: "auto",
      scrollbarColor: "auto",
    },
    "&&::-webkit-scrollbar": {
      height: 4,
    },
    "&&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&&::-webkit-scrollbar-thumb": {
      background: theme.palette.primary.main,
      borderRadius: 4,
    },
    // Firefox has no ::-webkit-scrollbar: the thinnest standard bar, in the same colour.
    "@supports (-moz-appearance: none)": {
      "&&": {
        scrollbarWidth: "thin",
        scrollbarColor: `${theme.palette.primary.main} transparent`,
      },
    },
  },
  crumbLink: {
    color: theme.palette.primary.main,
    fontFamily: "Satoshi-Bold",
    fontSize: "1rem",
    verticalAlign: "bottom",
    "&:hover": {
      color: theme.palette.text.primary,
    },
  },
  crumbText: {
    whiteSpace: "nowrap",
  },
  crumbCurrent: {
    color: theme.palette.text.primary,
    fontFamily: "Satoshi-Bold",
    fontSize: "1rem",
  },
}));

/**
 * Back button + breadcrumb trail for the files section.
 * crumbs: [{ key, label, onClick }], root first; the last one is the current page.
 * folderKey: the open folder. Deep trails collapse to first / … / parent / current;
 * MUI v4 can't collapse again after "…" is expanded, so each folder opened starts collapsed.
 */
export default function FilesBreadcrumbs({ crumbs, onBack, folderKey }) {
  const classes = useStyles();

  // A trail wider than the screen opens scrolled to its end, so the current
  // folder is in view. Re-run once the folder's path arrives.
  const crumbsRef = useRef(null);
  useEffect(() => {
    const el = crumbsRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [folderKey, crumbs.length]);

  return (
    <Box className={classes.trail}>
      <IconButton
        size="small"
        aria-label="Back"
        className={classes.backBtn}
        onClick={onBack}
      >
        <KeyboardBackspace />
      </IconButton>
      <Breadcrumbs
        key={folderKey}
        ref={crumbsRef}
        maxItems={4}
        itemsBeforeCollapse={1}
        itemsAfterCollapse={2}
        aria-label="breadcrumb"
        className={classes.crumbs}
      >
        {crumbs.map((crumb, index) =>
          index === crumbs.length - 1 ? (
            <Typography
              key={crumb.key}
              component="span"
              aria-current="page"
              title={crumb.label}
              className={`${classes.crumbText} ${classes.crumbCurrent}`}
            >
              {crumb.label}
            </Typography>
          ) : (
            <Link
              key={crumb.key}
              component="button"
              type="button"
              title={crumb.label}
              className={classes.crumbLink}
              onClick={crumb.onClick}
            >
              <span className={classes.crumbText}>{crumb.label}</span>
            </Link>
          )
        )}
      </Breadcrumbs>
    </Box>
  );
}
